import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { UserProvider, useUser } from './contexts/UserContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { AuthProvider } from './contexts/AuthContext';
import { KYCProvider } from './contexts/KYCContext';
import { QueryClientProvider, queryClient } from './config/queryClient';

// Import components
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout/Layout';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import PropertiesPage from './pages/PropertiesPage';
import PropertyDetail from './pages/PropertyDetail';
import Wallet from './pages/Wallet';
import Blockchain from './pages/Blockchain';
import PortfolioPage from './components/Portfolio/PortfolioPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminOfferings from './pages/AdminOfferings';
import LoadingSpinner from './components/UI/LoadingSpinner';
import AuthCallback from './pages/AuthCallback';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Landing from './pages/Landing';
import WalletTest from './pages/WalletTest';
import AdminLayout from './pages/AdminLayout';
import AdminTreasury from './pages/AdminTreasury';
import HiveFund from './pages/HiveFund';
import HiveFundInvest from './pages/HiveFundInvest';
import OfferingInvest from './pages/OfferingInvest';
import About from './pages/About';
import { InvestorPortal } from './components/new-components/InvestorPortal';
import { DemoPortal } from './components/new-components/DemoPortal';
import { PortalDashboardContent } from './components/new-components/PortalDashboardContent';
import { AccountSettings } from './pages/AccountSettings';
import { InvestLayout } from './components/new-components/InvestLayout';
import Transactions from './pages/Transactions';
import TransactionHistory from './pages/TransactionHistory';
import KYCSuccess from './pages/KYCSuccess';
import EmailVerification from './pages/EmailVerification';
import VerifyEmailRequired from './pages/VerifyEmailRequired';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { useRoleAccess } from './contexts/UserContext';
import ScrollToTop from './components/ScrollToTop';
import CryptoFunding from './pages/CryptoFunding';
import ShareConversion from './pages/ShareConversion';
import { TENANT } from './config';
import EMergeDemo from './pages/emerge/EMergeDemo';
import Activate from './pages/Activate';
import ReplaceAuthenticator from './pages/ReplaceAuthenticator';
import AccountSecurity from './pages/AccountSecurity';
import WorkspaceMenu from './components/WorkspaceMenu';
import WorkspaceHome from './pages/WorkspaceHome';
import EmergeLanding from './pages/emerge/EmergeLanding';
import EmergeEntityPage from './pages/admin/emerge/EmergeEntityPage';
import ProviderShell from './pages/provider/ProviderShell';
import AdvocateShell from './pages/advocate/AdvocateShell';
import UmedShell from './pages/umed/UmedShell';
import ManifestShell from './pages/manifest/ManifestShell';
import FinanceShell from './pages/finance/FinanceShell';
import DialerShell from './pages/dialer/DialerShell';
import DialerLogin from './pages/dialer/DialerLogin';
import SchedulingInbox from './pages/scheduling/SchedulingInbox';

// Admin deal flow pages
import AdminDealDashboard from './pages/admin/AdminDealDashboard';
import CapitalRaises from './pages/admin/CapitalRaises';
import CapitalRaiseDetail from './pages/admin/CapitalRaiseDetail';
import Projects from './pages/admin/Projects';
import ProjectDetail from './pages/admin/ProjectDetail';
import Contacts from './pages/admin/Contacts';
import ContactDetail from './pages/admin/ContactDetail';
import ESignatures from './pages/admin/ESignatures';
import SignDocument from './pages/SignDocument';
import SignSession from './pages/SignSession';
// Communications merged into Campaigns
import Engagements from './pages/admin/Engagements';
import AdminTransactions from './pages/admin/AdminTransactions';
import AdminAssets from './pages/admin/AdminAssets';
import AdminDocuments from './pages/admin/AdminDocuments';
import AdminUsers from './pages/admin/AdminUsers';
import AdminAccessRequests from './pages/admin/AdminAccessRequests';
import Campaigns from './pages/admin/Campaigns';
import EventFunnels from './pages/admin/EventFunnels';
import BusinessCards from './pages/admin/BusinessCards';
import EmergeFacilityMap from './pages/admin/EmergeFacilityMap';
import AccessReview from './pages/admin/AccessReview';
import SignatureConsole from './pages/admin/SignatureConsole';
import CoverageBoard from './pages/admin/emerge/CoverageBoard';
import FeedbackTriage from './pages/admin/emerge/FeedbackTriage';
import WorkflowHealth from './pages/admin/emerge/WorkflowHealth';
import AccountRecovery from './pages/admin/emerge/AccountRecovery';
import SystemAdmin from './pages/admin/SystemAdmin';
import BusinessCard from './pages/BusinessCard';
import AdminCalendar from './pages/admin/AdminCalendar';
import BusinessDevelopment from './pages/admin/bd/BusinessDevelopment';
import RecordFlow from './pages/admin/flow/RecordFlow';
import RecordFlowPortal from './pages/admin/flow/RecordFlowPortal';
import CommandCenter from './pages/admin/command/CommandCenter';
import WorkstreamDetailPage from './pages/admin/command/WorkstreamDetail';
import WeeklyReview from './pages/admin/command/WeeklyReview';
import CapitalDesk from './pages/admin/CapitalDesk';
import Vendors from './pages/admin/Vendors';
import Payroll from './pages/admin/Payroll';
import PageBuilder from './pages/admin/PageBuilder';
import SandboxSetup from './pages/admin/SandboxSetup';
import SandboxTenants from './pages/admin/SandboxTenants';
import AMLCompliance from './pages/admin/AMLCompliance';
import AdminCounterSign from './pages/admin/AdminCounterSign';
import SandboxInvestorAccept from './pages/SandboxInvestorAccept';
import AdminRiskDashboard from './pages/AdminRiskDashboard';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import Documentation from './pages/Documentation';
import MyPayments from './pages/MyPayments';
import PaymentLink from './pages/PaymentLink';
import BuildingCheckout from './pages/BuildingCheckout';
import PrivateInvestAccess from './pages/PrivateInvestAccess';
import TenantLogin from './pages/TenantLogin';
import TenantPortalLayout from './pages/TenantPortalLayout';
import SitePage from './pages/SitePage';
import PagePreview from './pages/PagePreview';
import SitePages from './pages/admin/SitePages';
import EventDealRoom from './pages/EventDealRoom';
import OnboardingCallBooking from './pages/OnboardingCallBooking';

// Import styles
import './index.css';

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireRole?: 'user' | 'investor' | 'admin';
  requireKyc?: boolean;
  requireEmailVerified?: boolean;
  /** Where to sign in when there is no session. A workspace with its own sign-in page names it. */
  loginPath?: string;
}

function ProtectedRoute({ 
  children, 
  requireAuth = true, 
  requireRole,
  requireKyc = false,
  requireEmailVerified = false,
  loginPath = '/login'
}: ProtectedRouteProps) {
  const { isAuthenticated, user, isLoading } = useUser();
  const roleAccess = useRoleAccess();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Check authentication requirement
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Check email verification requirement
  if (requireEmailVerified && user && !user.email_verified) {
    return <Navigate to="/verify-email-required" state={{ from: location }} replace />;
  }

  // Check role requirement
  if (requireRole) {
    // Admin users can access all routes
    if (user?.role === 'admin') {
      // granted
    } else if (!user || user.role !== requireRole) {
      // Redirect to home if user doesn't have required role
      return <Navigate to="/" replace />;
    }
  }

  // Check KYC requirement
  if (requireKyc && !roleAccess.isKycVerified) {
    return <Navigate to="/dashboard?kyc=required" replace />;
  }

  return <>{children}</>;
}


// Main App Router Component
function AppRouter() {
  const { isAuthenticated, user } = useUser();

  // Function to determine default route based on user role
  const getDefaultRoute = () => {
    if (!isAuthenticated) return '/';
    // eMerge decides from ratified capability on the server (WorkspaceHome),
    // never from `users.role`: this redirect used to fire the moment a
    // session existed and send every workforce user to /portal.
    if (TENANT.id === 'emerge') return '/home';

    switch (user?.role) {
      case 'admin':
        return '/admin';
      case 'investor':
      case 'user':
      default:
        return '/portal';
    }
  };

  return (
    <ErrorBoundary>
    <Router>
      <ImpersonationBanner />
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        
        {/* Building branded checkout */}
        <Route path="/building/pay/:sessionKey" element={<BuildingCheckout />} />

        {/* Payment Link - Public Route */}
        <Route path="/pay/:token" element={<PaymentLink />} />

        {/* Private Invest Access - Non-accredited investor approved link */}
        <Route path="/invest-access/:token" element={<PrivateInvestAccess />} />

        {/* Event Deal Room Funnel - Public QR campaign flow */}
        <Route path="/event/:slug" element={<EventDealRoom />} />
        {/* Public digital business card — what a printed QR opens. */}
        <Route path="/card/:slug" element={<BusinessCard />} />
        <Route path="/investor-access/:slug" element={<EventDealRoom />} />

        {/* E-Sign - Public Route */}
        {/* Declared BEFORE /sign/:id — "session" would otherwise be matched as
            a signature id and the packet link would render a not-found page.
            React Router ranks static above dynamic, but the two routes read
            better in the order they resolve. */}
        <Route path="/sign/session/:token" element={<SignSession />} />
        <Route path="/sign/:id" element={<SignDocument />} />

        {/* Sandbox Investor Accept - Public Route */}
        {/* Workforce account setup. Public by design: the activation code IS
            the credential, and the person has no account to log into yet. */}
        <Route path="/activate" element={<Activate />} />
        {/* After a backup-code sign-in: the ticket arrives in router state,
            never in the URL, and can only register a new phone. */}
        <Route path="/activate/replace" element={<ReplaceAuthenticator />} />
        <Route
          path="/account/security"
          element={
            <ProtectedRoute>
              {TENANT.id === 'emerge' && <WorkspaceMenu />}
              <AccountSecurity />
            </ProtectedRoute>
          }
        />
        <Route path="/sandbox/accept" element={<SandboxInvestorAccept />} />
        
        {/* Email Verification - Public Route */}
        <Route path="/verify-email" element={<EmailVerification />} />
        
        {/* Email Verification Required - For unverified users */}
        <Route path="/verify-email-required" element={<VerifyEmailRequired />} />

        {/* Password reset - Public Routes */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* Privacy Policy - Public Route */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        
        {/* Terms of Service - Public Route */}
        <Route path="/terms" element={<TermsOfService />} />

        {/* Demo Portal - Public Route */}
        <Route
          path="/demo"
          element={<DemoPortal />}
        />

        {/* About - Public Route */}
        <Route path="/about" element={<About />} />

        {/* Hive Fund - Public Routes */}
        <Route path="/hive-fund" element={<HiveFund />} />
        <Route
          path="/hive-fund/invest"
          element={<HiveFundInvest />}
        />

        {/* Site Pages — dynamic marketing/landing pages built in the admin Page Builder */}
        <Route path="/p/:slug" element={<SitePage />} />

        {/* Page Preview — admin draft preview, accessible without auth requirement */}
        <Route path="/preview/:slug" element={<PagePreview />} />
        
        {/* Landing Page - Public Route for unauthenticated users */}
        {/* Only redirect verified users to portal — unverified authenticated users
            can still reach the landing page to avoid a redirect loop with /verify-email-required */}
        {/* Honeyshares tenant skips landing page entirely — always redirect to /login */}
        <Route
          path="/"
          element={
            isAuthenticated && user?.email_verified ? (
              <Navigate to={TENANT.id === 'emerge' ? '/home' : '/portal'} replace />
            ) : TENANT.id === 'honeyshares' ? (
              <Navigate to="/login" replace />
            ) : TENANT.id === 'emerge' ? (
              <EmergeLanding />
            ) : (
              <Landing />
            )
          }
        />
        
        {/* Profile/Settings - Redirect to portal settings */}
        <Route path="/profile" element={<Navigate to="/portal/settings" replace />} />

        {/* Transactions History - Protected Route */}
        <Route
          path="/transactions"
          element={
            <ProtectedRoute requireEmailVerified>
              <Navigate to="/portal/transactions" replace />
            </ProtectedRoute>
          }
        />
        
        {/* Base + per-step URLs render the same screen, so browser Back/Forward, refresh,
            and deep-linking work within the multi-step flow. */}
        <Route
          path="/login/:step?"
          element={
            isAuthenticated ? (
              user?.email_verified ? (
                // eMerge: resolve in place, so a deep link carried in
                // location.state (ProtectedRoute's `from`) is still honoured.
                TENANT.id === 'emerge' ? <WorkspaceHome /> : <Navigate to={getDefaultRoute()} replace />
              ) : (
                // Logged in but unverified → straight to verify (no wizard flash on Back).
                <Navigate to="/verify-email-required" replace />
              )
            ) : (
              <Login />
            )
          }
        />

        <Route
          path="/register/:step?"
          element={
            isAuthenticated ? (
              user?.email_verified ? (
                <Navigate to={getDefaultRoute()} replace />
              ) : (
                // Logged in but unverified → straight to verify (no wizard flash on Back).
                <Navigate to="/verify-email-required" replace />
              )
            ) : TENANT.features.investing ? (
              <Register />
            ) : (
              // Public self-signup is the investor wizard: profile, then
              // accreditation. A hearing-care workforce does not register
              // itself; accounts are seeded and activated by invitation at
              // /activate. Anyone who lands here has the wrong door, and the
              // right one is where the invitation email points.
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Legacy property routes - redirect to portal */}
        <Route path="/feed" element={<Navigate to="/portal/invest/properties" replace />} />
        <Route path="/properties/:id" element={<Navigate to="/portal/invest/properties/:id" replace />} />
        <Route path="/property/:id" element={<Navigate to="/portal/invest/properties/:id" replace />} />
        
        {/* Portfolio page - redirect to portal */}
        <Route path="/portfolio" element={<Navigate to="/portal/portfolio" replace />} />
        
        {/* Wallet page - requires authentication */}
        <Route
          path="/wallet"
          element={
            <ProtectedRoute requireEmailVerified>
              <Wallet />
            </ProtectedRoute>
          }
        />

        {/* ============================================ */}
        {/* PROVIDER PORTAL - the field-facing surface  */}
        {/* ============================================ */}
        {/*
          Mounted at its own root rather than under /portal, because a provider
          is not a smaller admin: the shell, the navigation and the data are
          different, and nesting would inherit chrome that means nothing in a
          facility. What the caller may open is decided server-side by
          Authorization V2 — these routes render whatever the day sheet API
          consents to and nothing more.
        */}
        {TENANT.id === 'emerge' && (
          <>
            <Route
              path="/provider"
              element={
                <ErrorBoundary>
                  <ProtectedRoute requireEmailVerified>
                    <ProviderShell />
                  </ProtectedRoute>
                </ErrorBoundary>
              }
            />
            <Route
              path="/provider/:facilityId"
              element={
                <ErrorBoundary>
                  <ProtectedRoute requireEmailVerified>
                    <ProviderShell />
                  </ProtectedRoute>
                </ErrorBoundary>
              }
            />
          </>
        )}

        {/* ============================================ */}
        {/* PORTAL ROUTES - Canonical user area */}
        {/* ============================================ */}
        {TENANT.id === 'emerge' ? (
          <>
          {/* EP-005: where a signed-in person goes. Bare /portal used to fall
              into the wildcard below and render the executive demo shell. */}
          <Route
            path="/home"
            element={
              <ErrorBoundary>
                <ProtectedRoute requireEmailVerified>
                  <WorkspaceHome />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route path="/portal" element={<Navigate to="/home" replace />} />
          {/* Account settings, ahead of the wildcard below.
             
              `/portal/*` hands every unmatched path to EMergeDemo, whose
              pageFromPath() falls back to 'command' — so /portal/settings
              rendered the executive Command Center instead of the settings
              screen, and the two-factor enrolment UI behind it was
              unreachable for every eMerge user. A SYSTEM_ADMIN could not add
              a second factor through the product.
             
              React Router ranks a static segment above a splat, so these win
              over /portal/* without reordering anything. */}
          <Route
            path="/portal/settings"
            element={
              <ErrorBoundary>
                <ProtectedRoute requireEmailVerified>
                  {TENANT.id === 'emerge' && <WorkspaceMenu />}
                  <AccountSettings />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path="/portal/settings/:section"
            element={
              <ErrorBoundary>
                <ProtectedRoute requireEmailVerified>
                  {TENANT.id === 'emerge' && <WorkspaceMenu />}
                  <AccountSettings />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          {/* EP-004C — the Patient Advocate workspace. A static segment, so it
              wins over /portal/* below. Read-only; the server decides every
              row through ADVOCACY_LEAD_VIEW and OPERATIONAL_CASE assignments. */}
          <Route
            path="/portal/patient-advocate"
            element={
              <ErrorBoundary>
                <ProtectedRoute requireEmailVerified>
                  <AdvocateShell />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          {/* EP-004U phase 2 — the UMED operations workspace. A static
              segment, so it wins over /portal/* below. Read-only; the server
              decides every row through UMED_CASE_VIEW and the caller's
              facility scope, and defines no write route at all. */}
          <Route
            path="/portal/umed"
            element={
              <ErrorBoundary>
                <ProtectedRoute requireEmailVerified>
                  <UmedShell />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          {/* Manifest review — the morning that turns yesterday's provider
              visit into today's leads. A static segment, so it wins over
              /portal/* below. The server decides every row through
              MANIFEST_REVIEW and the caller's facility scope, and creating
              a lead is a second, separate capability. */}
          <Route
            path="/portal/manifest"
            element={
              <ErrorBoundary>
                <ProtectedRoute requireEmailVerified>
                  <ManifestShell />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          {/* The Dialer: calls on the eMerge Amazon Connect line. A static
              segment, so it wins over /portal/* below. The server decides
              access through COMMUNICATIONS_OUTBOUND_CALL_PLACE. */}
          {/* The Dialer's own sign-in: the one link a calling assistant keeps.
              Public by design; it lands on the phone, not on a workspace. */}
          <Route
            path="/portal/dialer/login"
            element={
              <ErrorBoundary>
                <DialerLogin />
              </ErrorBoundary>
            }
          />
          <Route
            path="/portal/dialer"
            element={
              <ErrorBoundary>
                <ProtectedRoute requireEmailVerified loginPath="/portal/dialer/login">
                  <DialerShell />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          {/* Workforce write cutover (2026-09-13): Finance's resident context and Scheduling's inbox. */}
          <Route
            path="/portal/finance"
            element={
              <ErrorBoundary>
                <ProtectedRoute requireEmailVerified>
                  <FinanceShell />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          <Route
            path="/portal/scheduling"
            element={
              <ErrorBoundary>
                <ProtectedRoute requireEmailVerified>
                  <SchedulingInbox />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          {/* Record Flow for managers who are not administrators. A static
              segment, so it wins over /portal/* below. The server decides
              through RECORD_FLOW_VIEW and PHI access (the same shapes as the
              admin console's, from /api/v1/record-flow), like every workspace. */}
          <Route
            path="/portal/record-flow"
            element={
              <ErrorBoundary>
                <ProtectedRoute requireEmailVerified>
                  <RecordFlowPortal />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          {/* eMerge's dashboard is a self-contained shell (own nav/topbar) with
              its own internal sub-navigation (see pageFromPath/go() in
              EMergeDemo.tsx) rather than nesting under InvestorPortal's
              investment-specific layout — one wildcard route is sufficient. */}
          <Route
            path="/portal/*"
            element={
              <ErrorBoundary>
                <ProtectedRoute requireEmailVerified>
                  <EMergeDemo />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
          </>
        ) : (
          <Route
            path="/portal"
            element={
              <ErrorBoundary>
                <ProtectedRoute requireEmailVerified>
                  <InvestorPortal />
                </ProtectedRoute>
              </ErrorBoundary>
            }
          >
            {/* Redirect /portal to /portal/invest/properties */}
            <Route index element={<Navigate to="invest/properties" replace />} />

            {/* Invest section with tabs - InvestLayout handles tab content */}
            <Route path="invest/*" element={<InvestLayout />} />

            {/* Offering invest flow — reached from InvestCtaBlock on Page Builder pages */}
            <Route path="invest/offering/:slug" element={<OfferingInvest />} />

            {/* Portfolio section */}
            <Route path="portfolio" element={<PortalDashboardContent />} />

            {/* Transactions section */}
            <Route path="transactions" element={<Transactions />} />

            {TENANT.features.crypto && (
              <>
                {/* Crypto Funding section */}
                <Route path="funding" element={<CryptoFunding />} />
              </>
            )}

            {TENANT.features.shareConversion && (
              <>
                {/* Share Conversion section */}
                <Route path="share-conversion" element={<ShareConversion />} />
              </>
            )}

            {/* My Payments - Engagement transaction history */}
            <Route path="payments" element={<MyPayments />} />

            {/* Native investor onboarding call booking */}
            <Route path="onboarding-call" element={<OnboardingCallBooking />} />

            {/* Settings section */}
            <Route path="settings" element={<AccountSettings />} />
            <Route path="settings/:section" element={<AccountSettings />} />
          </Route>
        )}

        {/* ============================================ */}
        {/* PUBLIC INVEST ROUTES - View properties without auth */}
        {/* ============================================ */}
        <Route path="/invest" element={<InvestLayout isPublic={true} />} />
        <Route path="/invest/:tab" element={<InvestLayout isPublic={true} />} />

        {/* ============================================ */}
        {/* LEGACY REDIRECTS - Point to canonical /portal */}
        {/* ============================================ */}
        <Route path="/investor" element={<Navigate to="/portal/invest/properties" replace />} />
        <Route path="/dashboard" element={<Navigate to="/portal/invest/properties" replace />} />

        {/* Wallet Test Page - Debug wallet access */}
        <Route
          path="/wallet-test"
          element={
            <ProtectedRoute requireEmailVerified>
              <WalletTest />
            </ProtectedRoute>
          }
        />

        {/* Blockchain page - public access */}
        <Route
          path="/blockchain"
          element={<Blockchain />}
        />

        {/* ── Legacy route redirects (/app/* → /portal/*) ── */}
        <Route path="/app/portfolio" element={<Navigate to="/portal/portfolio" replace />} />

        {/* Legacy redirects — routes migrated to /portal/* */}
        {/* NOTE: /app/transactions and /app/my-payments already redirect inline below. */}
        {/* /app/properties, /app/properties/:id, /app/payment-history */}
        {/* have no confirmed /portal/* equivalents yet — redirects will be added in M3. */}

        {/* Protected Routes with Layout */}
        <Route
          path="/app"
          element={
            <ProtectedRoute requireEmailVerified>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Properties - Public access to view, investor access to invest */}
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="properties/:id" element={<PropertyDetail />} />
          
          {/* Portfolio - Requires investment history */}
          <Route
            path="portfolio"
            element={
              <ProtectedRoute requireRole="investor">
                <PortfolioPage />
              </ProtectedRoute>
            }
          />
          
          {/* Transactions - Available to all authenticated users */}
          <Route path="transactions" element={<Navigate to="/portal/transactions" replace />} />
          
          {/* Payment Transaction History - ACH payments for HiveFund */}
          <Route path="payment-history" element={
            <ProtectedRoute>
              <TransactionHistory />
            </ProtectedRoute>
          } />

          {/* My Payments - redirect to portal */}
          <Route path="my-payments" element={<Navigate to="/portal/payments" replace />} />
        </Route>

        {/* Admin Routes - With AdminLayout wrapper for nested routes */}
        <Route
          path="/admin"
          element={
            <ErrorBoundary>
              <ProtectedRoute requireRole="admin">
                <AdminLayout />
              </ProtectedRoute>
            </ErrorBoundary>
          }
        >
          {/* Where /admin lands.
              AdminDealDashboard reports funds under management, distributions
              paid, active investors and open capital calls — every figure on it
              is a syndication measure, and all four of its queries hit tables
              eMerge does not have. It was the eMerge admin home, so signing in
              as an administrator was what produced the deal-flow 500s. eMerge
              lands on the facility map instead, which is the operational
              overview this business actually runs on. */}
          <Route
            index
            element={<Navigate to={TENANT.id === 'emerge' ? '/admin/emerge-map' : '/admin/dashboard'} replace />}
          />

          {/* Admin Deal Flow Dashboard.
              The path stays registered for every tenant on purpose. Login,
              the brand button, SandboxSetup and the admin catch-all all send
              people to /admin/dashboard; unregistering it for eMerge would
              drop them onto the catch-all, which redirects to /admin/dashboard
              — a loop. So eMerge keeps the address and gets sent onward. */}
          <Route
            path="dashboard"
            element={
              TENANT.id === 'emerge'
                ? <Navigate to="/admin/emerge-map" replace />
                : <AdminDealDashboard />
            }
          />

          {/* Capital Raises and Projects — the syndication surface.
              Hidden from the eMerge nav already, but the routes were still
              registered, so anything holding a link or a bookmark rendered a
              page whose every query targets tables eMerge does not have.
              Gated here too, so nav and routing tell the same story. Untouched
              for REDEX and Honeyshares, where the schema and the concepts are
              both real. */}
          {TENANT.id !== 'emerge' && (
            <>
              <Route path="capital-raises" element={<CapitalRaises />} />
              <Route path="capital-raises/:id" element={<CapitalRaiseDetail />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:id" element={<ProjectDetail />} />
            </>
          )}

          {/* Contacts CRM */}
          <Route path="contacts" element={<Contacts />} />
          <Route path="contacts/:id" element={<ContactDetail />} />

          {/* E-Signatures */}
          <Route path="esignatures" element={<ESignatures />} />

          {/* Counter-Signatures — Manager acceptance of subscription agreements */}
          <Route path="countersign" element={<AdminCounterSign />} />

          {/* Engagements (Payment Requests) */}
          <Route path="engagements" element={<Engagements />} />

          {/* Vendors */}
          <Route path="vendors" element={<Vendors />} />

          {/* Payroll */}
          <Route path="payroll" element={<Payroll />} />

          {/* Transactions (Merchant Report & Reconciliation) */}
          <Route path="transactions" element={<AdminTransactions />} />

          {/* Legacy Admin Dashboard (old monolithic) */}
          <Route
            path="legacy-dashboard"
            element={<AdminDashboard />}
          />
          
          {/* Admin Treasury */}
          <Route
            path="wallet"
            element={<AdminTreasury />}
          />
          
          {/* Admin Assets / Properties */}
          <Route
            path="properties"
            element={<AdminAssets />}
          />

          {/* Admin Offerings */}
          <Route
            path="offerings"
            element={<AdminOfferings />}
          />

          {/* Campaign Orchestration */}
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="business-cards" element={<BusinessCards />} />
          <Route path="event-funnels" element={<EventFunnels />} />

          {/* Mounted on its own path rather than under emerge/ — the dynamic
              route below would otherwise treat "map" as an entity name. */}
          <Route path="emerge-map" element={<EmergeFacilityMap />} />
          <Route path="system" element={<SystemAdmin />} />

          {/* Declared BEFORE emerge/:entity below. React Router ranks a static
              segment above a dynamic one, so this would win either way — but
              relying on that is a trap for whoever adds the next fixed page
              under emerge/, and "access-review" is not an entity. */}
          <Route path="emerge/access-review" element={<AccessReview />} />
          <Route path="emerge/signatures" element={<SignatureConsole />} />
          <Route path="emerge/coverage" element={<CoverageBoard />} />
          <Route path="emerge/feedback" element={<FeedbackTriage />} />
          <Route path="emerge/workflow-health" element={<WorkflowHealth />} />
          <Route path="emerge/account-recovery" element={<AccountRecovery />} />

          {/* eMerge Operations — single dynamic route backing every entity
              in the "eMerge Operations" nav group (see AdminLayout.tsx) */}
          <Route path="emerge/:entity" element={<EmergeEntityPage />} />

          {/* Ops Calendar */}
          <Route path="calendar" element={<AdminCalendar />} />
          <Route path="business-development" element={<BusinessDevelopment />} />
          <Route path="record-flow" element={<RecordFlow />} />

          {/* eMerge Operational Command Center — executive operating view over
              the company master plan (workstreams, critical path, commitments) */}
          <Route path="command" element={<CommandCenter />} />
          <Route path="command/workstream/:id" element={<WorkstreamDetailPage />} />
          <Route path="command/weekly-review" element={<WeeklyReview />} />

          {/* Documents */}
          <Route path="documents" element={<AdminDocuments />} />

          {/* Users */}
          <Route path="users" element={<AdminUsers />} />

          {/* Access Requests (Non-Accredited Investors) */}
          <Route path="access-requests" element={<AdminAccessRequests />} />

          {/* Risk & Compliance Dashboard */}
          <Route path="risk" element={<AdminRiskDashboard />} />

          {/* Capital Desk — Deal Engine (REDEX only) */}
          <Route path="capital-desk" element={<CapitalDesk />} />

          {/* Documentation — Legal & Compliance */}
          <Route path="documentation" element={<Documentation />} />

          {/* Page Builder */}
          <Route path="page-builder" element={<PageBuilder />} />

          {/* Site Pages — list view and editor aliases */}
          <Route path="pages" element={<SitePages />} />
          <Route path="pages/new" element={<PageBuilder />} />
          <Route path="pages/:templateId/edit" element={<PageBuilder />} />

          {/* Sandbox Setup Wizard + Tenant Management */}
          <Route path="sandbox/setup" element={<SandboxSetup />} />
          <Route path="sandbox/tenants" element={<SandboxTenants />} />

          {/* REDEX Shield — AML Compliance */}
          <Route path="aml" element={<AMLCompliance />} />

          {/* Catch all admin subroutes */}
          <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
        </Route>

        
        {/* KYC Completion Route - Handles redirect from Stripe Identity */}
        <Route
          path="/kyc/complete"
          element={
            <ProtectedRoute>
              <KYCSuccess />
            </ProtectedRoute>
          }
        />

        {/* ============================================ */}
        {/* TENANT PORTAL ROUTES — Sandbox white-label  */}
        {/* app.redexct.com/:tenantSlug/login            */}
        {/* app.redexct.com/:tenantSlug/portal/*         */}
        {/* Must be LAST — dynamic :tenantSlug segment   */}
        {/* ============================================ */}

        {/* Tenant-branded login */}
        <Route path="/:tenantSlug/login" element={<TenantLogin />} />

        {/* Investor invite acceptance via tenant URL */}
        <Route path="/:tenantSlug/accept" element={<SandboxInvestorAccept />} />

        {/* Tenant portal — all investor pages with white-label branding */}
        <Route path="/:tenantSlug/portal" element={<TenantPortalLayout />}>
          <Route index element={<Navigate to="invest/properties" replace />} />
          <Route path="invest/*" element={<InvestLayout />} />
          <Route path="portfolio" element={<PortalDashboardContent />} />
          <Route path="transactions" element={<Transactions />} />
          <Route path="payments" element={<MyPayments />} />
          <Route path="settings" element={<AccountSettings />} />
          <Route path="settings/:section" element={<AccountSettings />} />
        </Route>

        {/* 404 Route */}
        <Route
          path="*"
          element={
            <div className="min-h-screen bg-background flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-foreground mb-4">404</h1>
                <p className="text-muted-foreground mb-6">Page not found</p>
                <button
                  onClick={() => window.history.back()}
                  className="btn-primary"
                >
                  Go Back
                </button>
              </div>
            </div>
          }
        />
      </Routes>
    </Router>
    </ErrorBoundary>
  );
}

// Main App Component
function App() {
  return (
    <AuthProvider>
      <KYCProvider>
        {/* UserProvider kept for backward-compat during migration — see src/contexts/MIGRATION.md */}
        <UserProvider>
          {/* One notification poll for the whole app: the bell in the
              workspace bar and the workspaces underneath it read the same
              answer, and nothing polls until somebody is signed in. */}
          <NotificationsProvider>
          <div className="App">
        <AppRouter />
        
        {/* Global Toast Notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 5000,
            style: {
              background: '#fff',
              color: '#333',
              borderRadius: '12px',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e5e5e5',
            },
            success: {
              iconTheme: {
                primary: '#22c55e',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
            loading: {
              iconTheme: {
                primary: '#3b82f6',
                secondary: '#fff',
              },
            },
          }}
        />
          </div>
          </NotificationsProvider>
        </UserProvider>
      </KYCProvider>
    </AuthProvider>
  );
}

/**
 * Web3 is an optional extension, so its providers are code-split and only
 * loaded for tenants that declare crypto support. The lazy import matters
 * more than the conditional render: config/web3modal.ts initializes
 * WalletConnect and the Web3Modal singleton at module scope, so a static
 * import would start that machinery for eMerge too, no matter what we
 * rendered (EP-001A / D-06).
 */
const Web3Providers = React.lazy(() => import('./providers/Web3Providers'));

function AppWithProviders() {
  const cryptoEnabled = Boolean(TENANT.features?.crypto);

  return (
    <QueryClientProvider client={queryClient}>
      {cryptoEnabled ? (
        <React.Suspense fallback={null}>
          <Web3Providers>
            <App />
          </Web3Providers>
        </React.Suspense>
      ) : (
        <App />
      )}
    </QueryClientProvider>
  );
}

export default AppWithProviders;
